import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import shortid from 'shortid'
import {
  SortableContainer,
  SortableElement,
  // arrayMove,
} from 'react-sortable-hoc'
import classNames from 'classnames'

import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import styles from '../component-styles/dashboard-manage.module.scss'
import DeleteButtonWithConfirmation from './delete-button-with-confirmation'
import ConfirmationButtons from './confirmation-buttons'
import Button from './button'
import { getRemoteDashboardList } from '../store/actions'
import {
  addRemoteDashboard,
  deleteRemoteDashboard,
  updateRemoteDashboard,
} from '../api-client/report-client'
import Row, { FormRow, LabelSlot, FieldSlot } from './row'
import Heading from './heading'
import Input, { Label, ClickEditInput } from './input'
// import useMobile from '../hooks/useMobile'
import ToolTip from './tooltip'

interface SortableProps {
  children?: React.ReactElement
  itemIndex: number
  item: EmbedItem
  dispatch: any
}

const SortableItem = SortableElement(
  ({ children, itemIndex, item, dispatch }: SortableProps) => {
    return (
      <Row align="space-between" className={styles.row} odd={!(itemIndex % 2)}>
        <div className={styles.tableCell}>
          <span className={styles.tableCellContent}>
            <span className={styles.tableCellContentPadded}>
              <ClickEditInput
                className={styles.input}
                id={`dashboard-name-${itemIndex}`}
                name={`dashboard-name-${itemIndex}`}
                value={item.dashboardName}
                onChange={async (value: string): Promise<void> => {
                  const { dashboardID } = item
                  await updateRemoteDashboard({
                    dashboardID,
                    dashboardName: value,
                  })
                  dispatch(getRemoteDashboardList())
                }}
              />
              <ClickEditInput
                className={styles.input}
                id={`dashboard-link-${itemIndex}`}
                name={`dashboard-link-${itemIndex}`}
                value={item.embedLink}
                onChange={async (value: string): Promise<void> => {
                  const { dashboardID } = item
                  await updateRemoteDashboard({ dashboardID, embedLink: value })
                  dispatch(getRemoteDashboardList())
                }}
              />
            </span>
          </span>
        </div>
        <div className={styles.tableCell}>
          <img
            src={
              item.dashboardThumbnail !== ''
                ? item.dashboardThumbnail
                : '/powerbi-thumbnail.png'
            }
            className={styles.dashboardThumbnail}
            alt=""
          />
        </div>
        <div className={classNames(styles.tableCell, styles.tableCellButtons)}>
          {children}
        </div>
      </Row>
    )
  },
)

export function AddNewItem({
  dispatch,
  show = false,
  buttonOnly = false,
}: {
  dispatch: any
  show: boolean
  buttonOnly: boolean
}): React.ReactElement {
  const [showAdd, setShowAdd] = useState(show)
  const [dashboardName, setDashboardName] = useState('')
  const [embedLink, setEmbedLink] = useState('')
  const [error, setError] = useState('')

  return (
    <>
      {showAdd && (
        <Row
          align="space-between"
          className={classNames(styles.row, styles.addNewItemRow)}
        >
          <div className={classNames(styles.tableCell, styles.addNewItem)}>
            <span className={styles.tableCellContent}>
              <span className={styles.tableCellContentPadded}>
                <FormRow last>
                  <LabelSlot noPadding column>
                    <Label id="dashboard-name" modalHeading>
                      Dashboard name
                    </Label>
                  </LabelSlot>
                  <FieldSlot noPadding column>
                    <Input
                      className={styles.input}
                      id="dashboard-name"
                      name="dashboard-name"
                      value={dashboardName}
                      onChange={(
                        e: React.ChangeEvent<HTMLInputElement>,
                      ): void => {
                        const { value } = e.target as HTMLInputElement
                        setDashboardName(value)
                      }}
                    />
                  </FieldSlot>
                </FormRow>
                <FormRow last>
                  <LabelSlot noPadding column>
                    <Label id="dashboard-link" modalHeading>
                      Dashboard embed link
                    </Label>
                    <ToolTip
                      className={styles.tooltip}
                      message="Only Google Data Studio and PowerBI dashboards are enabled"
                    />
                  </LabelSlot>
                  <FieldSlot noPadding column>
                    <Input
                      className={styles.input}
                      id="dashboard-link"
                      name="dashboard-link"
                      value={embedLink}
                      // error={error !== ''}
                      onChange={(
                        e: React.ChangeEvent<HTMLInputElement>,
                      ): void => {
                        const { value } = e.target as HTMLInputElement
                        setEmbedLink(value)
                        // if (
                        //   value.search(
                        //     /^(https:\/\/datastudio.google.com\/)/gi,
                        //   ) === -1
                        // ) {
                        //   setError(
                        //     'Only Google Data Studio links are currenly supported.',
                        //   )
                        // } else {
                        //   setError('')
                        // }
                      }}
                    />
                    {error !== '' && <p className={styles.error}>{error}</p>}
                  </FieldSlot>
                </FormRow>
              </span>
              <span className={styles.tableCellContentPadded}>
                <Button
                  disabled={dashboardName === '' || embedLink === ''}
                  type="containerBlue"
                  className={styles.addNewButtonConfirm}
                  onClick={async (): Promise<void> => {
                    const res: any = await addRemoteDashboard({
                      embedLink,
                      dashboardName,
                    })
                    if (!res) {
                      setError(
                        'Error embedding dashboard, please check your URL and try again',
                      )
                    } else {
                      dispatch(getRemoteDashboardList())
                      setError('')
                      setShowAdd(false)
                    }
                  }}
                >
                  Add
                </Button>
              </span>
            </span>
          </div>
        </Row>
      )}
      <Row
        align="space-between"
        className={classNames(styles.row, styles.addNewItemButtonRow, {
          [styles.buttonOnly]: buttonOnly,
        })}
      >
        <div className={classNames(styles.tableCell, styles.tableCellButtons)}>
          <Button
            type="text"
            align="left"
            className={styles.addNewButton}
            onClick={() => {
              setShowAdd(!showAdd)
            }}
          >
            Add a new dashboard +
          </Button>
        </div>
      </Row>
    </>
  )
}

const SortableWrapper = SortableContainer(
  ({ dispatch, data }): React.ReactElement => {
    return (
      <div className={styles.tableBody}>
        {data.map((item, index) => {
          return (
            <SortableItem
              disabled
              dispatch={dispatch}
              index={index}
              key={shortid.generate()}
              itemIndex={index}
              item={item}
            >
              <>
                <DeleteButtonWithConfirmation
                  odd={!(index % 2)}
                  className={styles.deleteButtons}
                  onClick={async (): Promise<void> => {
                    const { dashboardID } = item
                    await deleteRemoteDashboard({
                      dashboardID,
                    })
                    dispatch(getRemoteDashboardList())
                  }}
                >
                  <p>Are you sure?</p>
                </DeleteButtonWithConfirmation>
              </>
            </SortableItem>
          )
        })}
        <AddNewItem
          dispatch={dispatch}
          show={data.length === 0}
          buttonOnly={data.length === 0}
        />
      </div>
    )
  },
)

interface Props {
  active: boolean
  toggleActive: () => void
  data: any
}

export default function Manage({
  active,
  toggleActive,
  data,
}: Props): React.ReactElement {
  // const explainData = useSelector((s: any): any => s.explain)
  // const isMobile = useMobile(769)
  const dispatch = useDispatch()

  // useEffect((): void => {
  //   if ((addNewMetric || editMetric) && metricsItems.length === 0) {
  //     dispatch(getGaMetrics())
  //   }
  //   // eslint-disable-next-line
  // }, [addNewMetric, editMetric])

  // const onSortEnd = ({ oldIndex, newIndex }) => {
  //   // dispatch(setExplainSetup())
  //   if (oldIndex !== newIndex) {
  //     const orderedArray = arrayMove(explainData.pageLayout, oldIndex, newIndex)
  //     dispatch(
  //       saveExplainSetup({
  //         ...explainData,
  //         pageLayout: orderedArray,
  //       }),
  //     )
  //   }
  // }

  if (!active) {
    return <></>
  }

  return (
    <>
      <Modal active={active} toggleActive={toggleActive} type="white">
        <ModalHead>
          <Heading type={3} align="left">
            Edit dashboards
          </Heading>
        </ModalHead>

        <ModalBody noPadding>
          {data.length === 0 && (
            <p className={styles.padded}>
              Please add your first custom report. Only Google Data Studio and
              PowerBI links are currently supported. Email support to add other
              dashboards.
            </p>
          )}
          <div className={classNames(styles.wrapper)}>
            <div className={styles.body}>
              <div className={styles.list}>
                <div className={styles.table}>
                  <SortableWrapper
                    data={data}
                    helperClass={styles.itemDragged}
                    dispatch={dispatch}
                    lockAxis="y"
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <ConfirmationButtons
            type="simple"
            centerAlign
            className={styles.confirmationButtons}
            onYes={(): void => {
              // setNewMetricItem({...defaultMetric})
              toggleActive()
            }}
            // onNo={(): void => {
            //   toggleActive()
            // }}
            yesLabel="OK"
            // noLabel="Go back"
          />
        </ModalFooter>
      </Modal>
    </>
  )
}
